/**
 * React Query hooks for Email operations.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@aaas/shared';
import emailService from '../services/email.service';
import type {
  EmailTemplateCreateInput,
  EmailTemplateUpdateInput,
  SendEmailInput,
  BulkSendEmailInput,
  PreviewEmailInput,
  ReplyEmailInput,
} from '../types';

// ── Template Queries ──

interface UseEmailTemplateListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useEmailTemplateListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  search,
  enabled = true,
  refetchInterval = false,
}: UseEmailTemplateListQueryParams) => {
  const skip = (page - 1) * size;
  return useQuery({
    queryKey: ['email-templates', workspaceId, page, size, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return await emailService.listTemplates(workspaceId, idToken, {
        skip,
        limit: size,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};

// ── Template Mutations ──

export const useCreateEmailTemplateMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (data: EmailTemplateCreateInput) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailService.createTemplate(currentWorkspace.id, idToken, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });
};

export const useUpdateEmailTemplateMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      templateId,
      data,
    }: {
      templateId: string;
      data: EmailTemplateUpdateInput;
    }) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailService.updateTemplate(
        templateId,
        currentWorkspace.id,
        idToken,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });
};

export const useDeleteEmailTemplateMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (templateId: string) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      await emailService.deleteTemplate(templateId, currentWorkspace.id, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });
};

// ── Send Mutations ──

export const useSendEmailMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (data: SendEmailInput) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailService.sendEmail(currentWorkspace.id, idToken, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-threads'] });
    },
  });
};

export const useBulkSendEmailMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (data: BulkSendEmailInput) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailService.bulkSendEmail(currentWorkspace.id, idToken, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-threads'] });
    },
  });
};

export const useReplyToThreadMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (data: ReplyEmailInput) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      const { thread_id, ...replyData } = data;
      return await emailService.replyToThread(
        thread_id,
        currentWorkspace.id,
        idToken,
        replyData
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['email-threads'] });
      queryClient.invalidateQueries({
        queryKey: ['email-thread', variables.thread_id],
      });
    },
  });
};

export const usePreviewEmailMutation = () => {
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (data: PreviewEmailInput) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailService.previewEmail(currentWorkspace.id, idToken, data);
    },
  });
};

// ── Thread Queries ──

interface UseEmailThreadListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  cvId?: string;
  jobId?: string;
  search?: string;
  direction?: 'sent' | 'received';
  page?: number;
  size?: number;
  skip?: number;
  limit?: number;
  enabled?: boolean;
}

export const useEmailThreadListQuery = ({
  workspaceId,
  idToken,
  cvId,
  jobId,
  search,
  direction,
  page,
  size,
  skip: skipOverride,
  limit: limitOverride,
  enabled = true,
}: UseEmailThreadListQueryParams) => {
  const limit = limitOverride ?? size ?? 50;
  const skip = skipOverride ?? (page ? (page - 1) * limit : 0);

  return useQuery({
    queryKey: ['email-threads', workspaceId, cvId, jobId, search, direction, skip, limit],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return await emailService.listThreads(workspaceId, idToken, {
        skip,
        limit,
        cv_id: cvId,
        job_id: jobId,
        search,
        direction,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval: 7_000,
    staleTime: 10000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseEmailThreadQueryParams {
  threadId: string | undefined;
  workspaceId?: string;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useEmailThreadQuery = ({
  threadId,
  workspaceId,
  idToken,
  enabled = true,
}: UseEmailThreadQueryParams) => {
  return useQuery({
    queryKey: ['email-thread', threadId, workspaceId],
    queryFn: async () => {
      if (!threadId || !workspaceId || !idToken) {
        throw new Error('Thread ID, workspace ID, and token are required');
      }
      return await emailService.getThread(threadId, workspaceId, idToken);
    },
    enabled: enabled && !!threadId && !!workspaceId && !!idToken,
    refetchInterval: 10_000,
    staleTime: 5000,
    gcTime: 10 * 60 * 1000,
  });
};
